import * as amplitudeBrowser from '@amplitude/analytics-browser';
import { AMPLITUDE_KEY } from '@/constants';
import { Metrics, MetricsContext } from '@/types/metrics';
import { CartItemWithProductVariantSize } from '@/types/cart';
import { productIdFromUrl } from '../navigation';

export const BASE_EVENT_NAME = 'whitelabel';
const eventKeys = {
  purchase: 'checkout.purchase.success',
  add_to_cart: 'product.added',
  remove_from_cart: 'cart_page.remove_item.clicked',
  view_item: 'product_page.viewed',
  select_item: 'recommended_products.clicked',
  view_cart: 'cart_page.viewed',
  begin_checkout: 'checkout.viewed',
  // add_shipment_info: true,
  // add_payment_info: true,
  // amplitude specific events
  color_clicked: 'product_color.clicked',
  size_clicked: 'product_size.clicked',
  mobile_nav_clicked: 'mobile_nav.clicked',
  search_results_clicked: 'search_results.clicked',
  description_accordion: 'product_page.description_accordion.clicked',
  productdetails_accordion: 'product_page.product_details_accordion.clicked',
  deliverydetails_accordion: 'product_page.deliverydetails_accordion.clicked',
  footer_link: 'footer_link.clicked',
  collection_page: 'collection_page.viewed',
  messaging_banner: 'messaging_banner.viewed',
  collection_desktop_nav_clicked: 'collection_desktop_nav.clicked',
  collection_mobile_menu_clicked: 'collection_mobile_menu.clicked',
  homepage_viewed: 'homepage.viewed',
  quickview_viewed: 'quickview.viewed',
  search_event: 'search_event',
  size_guide: 'product_page.size_guide.clicked',
  cart_link_clicked: 'cart_link.clicked',
  error_viewed: 'error.viewed',
  express_payment: 'express_payment.clicked',
  recommended_products_touched: 'recommended_products.touched',
  promo_code_clicked: 'checkout.have_promo_code.clicked',
  promo_code_applied: 'checkout.promo_code.applied',
  delivery_option_clicked: 'checkout.delivery_option.clicked',
  modify_order_clicked: 'checkout.modify_order.clicked',
  cart_summary_viewed: 'checkout.cart_summary.viewed',
  thank_you_page_viewed: 'thank_you_page.viewed',
  place_order_clicked: 'checkout.place_order.clicked',
  checkout_button_clicked: 'product_page.checkout_button.clicked',
  payment_method_clicked: 'checkout.payment_method.clicked',
};

const init = () => {
  return (
    amplitudeBrowser &&
    AMPLITUDE_KEY &&
    amplitudeBrowser.init(AMPLITUDE_KEY, undefined, {
      // logLevel: amplitudeBrowser.Types.LogLevel.Warn,
      defaultTracking: false,
    })
  );
};

const track = (eventName: keyof Metrics, eventProperties: any, context: MetricsContext) => {
  const selectProperties = {
    ...(eventProperties.subTotal && { cartValue: eventProperties.subTotal }),
    ...(eventProperties.source && { source: eventProperties.source }),
    ...(eventProperties.placement && { placement: eventProperties.placement }),
    ...(eventProperties.productId && { productId: eventProperties.productId }),
    ...(eventProperties.variationId && { variationId: eventProperties.variationId }),
    ...(eventProperties.price && { price: eventProperties.price }),
    ...(eventProperties.quantity && { quantity: eventProperties.quantity }),
    ...(eventProperties.position && { position: eventProperties.position }),
    ...(eventProperties.campaignRootId && { campaignRootId: eventProperties.campaignRootId }),
    ...(eventProperties.collectionName && { collectionName: eventProperties.collectionName }),
    ...(eventProperties.collectionUrl && { collectionUrl: eventProperties.collectionUrl }),
    ...(eventProperties.domainHost && { domainHost: eventProperties.domainHost }),
    ...(eventProperties.searchTerm && { searchTerm: eventProperties.searchTerm }),
    ...(eventProperties.errorTitle && { title: eventProperties.errorTitle }),
    ...(eventProperties.paymentType && { paymentType: eventProperties.paymentType }),
    ...(eventProperties.messageNumber && { messageNumber: eventProperties.messageNumber }),
    ...(eventProperties.errorId && { errorId: eventProperties.errorId }),
    ...(eventProperties.checkoutId && { checkoutId: eventProperties.checkoutId }),
    ...(eventProperties.orderId && { orderId: eventProperties.orderId }),
    ...(eventProperties.value && { orderTotal: eventProperties.value }),
    ...(eventProperties.discountAmount && { discountAmount: eventProperties.discountAmount }),
    ...(eventProperties.promoApplied && { promoApplied: eventProperties.promoApplied }),
    ...(eventProperties.promoCode && { promoCode: eventProperties.promoCode }),
  };

  if (!eventProperties.price && eventProperties.product?.primaryProduct?.[0]?.price) {
    selectProperties.price = eventProperties.product?.primaryProduct?.[0]?.price;
  } else if (!eventProperties.price && eventProperties.item) {
    selectProperties.price = eventProperties.item.price;
  }
  if (!eventProperties.productId && (eventProperties.product || eventProperties.item)) {
    const productId = eventProperties.product
      ? productIdFromUrl(eventProperties.product.url)
      : productIdFromUrl(eventProperties.item.url || '');
    selectProperties.productId = productId;
  }
  if (!eventProperties.productIds && eventName === 'view_cart' && eventProperties.validatedCartProducts) {
    const productIds = eventProperties.validatedCartProducts.map((p: CartItemWithProductVariantSize) => {
      return p.product?.primaryProductId;
    });
    selectProperties.productIds = productIds;
  } else if (!eventProperties.productId && eventName === 'search_results_clicked' && eventProperties.listingUrl) {
    const productId = productIdFromUrl(eventProperties.listingUrl);
    selectProperties.productId = productId;
  }

  const propertiesAndContext = { ...selectProperties, ...context };
  return eventKeys[eventName as keyof typeof eventKeys]
    ? amplitudeBrowser.track(
        `${BASE_EVENT_NAME}.${eventKeys[eventName as keyof typeof eventKeys]}`,
        propertiesAndContext
      )
    : null;
};

const amplitude = { init, track };

export default amplitude;
